import { CaseUtil } from '@vectord/utils';
import { type Edge, type Node } from '@xyflow/react';
import { EdgeType } from 'automations/data/edgeTypes';
import { combinedSubNodes, NodeType } from 'automations/data/nodeTypes';
import { ConditionTypes } from 'automations/types/automationConfig';

import { type GetAutomationTreeResponse } from '../queries/getAutomationTree';

const getNode = (nodeId: string, nodes: Node[]) => nodes.find((node) => node.id === nodeId);

export function getCustomEdgeSourceHandle(nodeSubType: ConditionTypes, targetNodeId: string, nodes: Node[]) {
  const targetNode = getNode(targetNodeId, nodes);
  if (nodeSubType === ConditionTypes.ifElse && targetNode?.data?.executionOrder === 1) {
    return 'if';
  } else if (nodeSubType === ConditionTypes.ifElse && targetNode?.data?.executionOrder === 2) {
    return 'else';
  }
  return null;
}

export function getEdgeType(sourceNodeId: string, targetNodeId: string, nodes: Node[]) {
  const sourceNode = getNode(sourceNodeId, nodes);
  const targetNode = getNode(targetNodeId, nodes);

  if (sourceNode?.data?.nodeSubType === ConditionTypes.ifElse) {
    if (targetNode?.data.executionOrder === 1) {
      return EdgeType.if;
    }
    return EdgeType.else;
  }

  return EdgeType.default;
}

export const parseAutomationTree = (
  tree: GetAutomationTreeResponse['automationTree'],
): { nodes: Node[]; edges: Edge[] } => {
  const nodes: Node[] = tree.map((node) => {
    const nodeData = node;
    const {
      nodeType,
      nodeSubType,
    } = nodeData;
    let parsedNodeType = NodeType.fallback;
    if (
      combinedSubNodes.includes(nodeSubType as any)
      && Object.values(NodeType).includes(nodeType as any)
    ) {
      parsedNodeType = nodeType as any;
    }

    return {
      id: node.id,
      data: {
        ...CaseUtil.toCase('camelCase', nodeData),
        nodeType: parsedNodeType,
        serverNodeType: nodeType,
      },
      type: parsedNodeType,
      position: { x: 0, y: 0 }, // Default position, will be adjusted by Positioning Logic
    };
  });

  const edges: Edge[] = tree.flatMap((node) => node.sourceEdges.map((edge) => ({
    id: edge.id,
    source: node.id,
    target: edge.targetNodeId.toString(),
    sourceHandle: getCustomEdgeSourceHandle(node.nodeSubType as ConditionTypes, edge.targetNodeId.toString(), nodes),
    type: getEdgeType(node.id, edge.targetNodeId.toString(), nodes),
  } as Edge)));

  return { nodes, edges };
};
